import { useCallback } from "react";
import { embedUrlOf, resolveEmbed } from "../../../features/embed/embedOptionsState";
import { useToast } from "../../../shared/ui";
import { useStudioModelId } from "../studioModel";
import { useConfiguratorDraft } from "./draft";

/**
 * "Open customer link" — the shared link a shopper gets, opened in a new tab.
 *
 * Vault models only: on /dev/studio there is no piece in the Vault to link to
 * (`available` is false and the callers hide the button). The draft is saved
 * first when it has unsaved choices, because the link reads what is SAVED —
 * opening it on a stale def would show the shopper something else.
 *
 * The tab is opened synchronously, inside the click, and pointed at the link
 * once the save lands: a `window.open` after an await is not the user's
 * gesture any more and a popup blocker swallows it.
 */
export function useCustomerLink(): { available: boolean; open: () => void } {
  const [, draft] = useConfiguratorDraft();
  const modelId = useStudioModelId();
  const { toast } = useToast();

  const open = useCallback(() => {
    if (!modelId) return;
    const tab = window.open("", "_blank");
    void (async () => {
      try {
        if (draft.dirty) await draft.save();
        const url = embedUrlOf(await resolveEmbed(modelId));
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
        } else {
          window.open(url, "_blank", "noopener");
        }
      } catch {
        tab?.close();
        toast({
          tone: "error",
          title: "Could not open the customer link",
          description: "Your choices are still here. Check your connection and try again.",
        });
      }
    })();
  }, [draft, modelId, toast]);

  return { available: !!modelId, open };
}
